import AppData from 'Data/AppData';
import { useAuthContext } from 'Data/context/Auth';
import { OBJECT_STATUS, user_org_roles } from 'Data/Enum';
import OrgData from 'Data/OrgData';
import DataManager from 'Lib/DataManager';
import PropTypes from 'prop-types';
import { createContext, useContext, useEffect, useState } from 'react';
import { canUserViewAllCores } from 'Util';
import { getTagValue } from 'Util/tagUtil';

const orgMap = new Map();
export const getOrgNameFromId = (orgId) => {
  return (orgId && orgMap.get(orgId)?.name) || '';
};

export const getPIForOrg = (id) => {
  return (id && orgMap.get(id)?.pi_id) || '';
};

export const getUserOrgIds = () => {
  return [
    ...new Set(AppData.organizationRoles().map((role) => +role.split('::')[0])),
  ];
};

export const getUserManagingOrgIds = () => {
  return [
    ...new Set(
      AppData.organizationRoles()
        .filter((role) => role.split('::')[1] === user_org_roles.MANAGER.key)
        .map((role) => +role.split('::')[0]),
    ),
  ];
};

export const doesOrgLinkResourcesToLineItems = (id) => {
  return id && orgMap.get(id)
    ? !!getTagValue(orgMap.get(id), 'line_item_resources')
    : false;
};

const OrgContext = createContext({
  fullOrgList: [],
  getFilteredList: () => [],
});
OrgContext.displayName = 'OrgContext';

let DM = new DataManager();
export const OrgStateProvider = ({ children }) => {
  const { isLoggedIn } = useAuthContext();
  const [fullOrgList, setFullOrgList] = useState();

  useEffect(() => {
    DM.add(
      OrgData.registerListener('ALL_ORGS_LOADED', fullOrgListLoaded),
      OrgData,
    );
    DM.add(
      OrgData.registerListener('USER_ORGS_LOADED', userOrgListLoaded),
      OrgData,
    );
    DM.add(
      OrgData.registerListener('RECORD_UPDATED', updateMapForOrg),
      OrgData,
    );
    DM.add(
      OrgData.registerListener('RECORD_ACTIVATED', updateMapForOrg),
      OrgData,
    );
    DM.add(
      OrgData.registerListener('RECORD_DEACTIVATED', updateMapForOrg),
      OrgData,
    );
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      if (canUserViewAllCores()) {
        OrgData.populateOrgs();
      } else {
        OrgData.populateUserOrgs();
      }
    } else {
      setFullOrgList([]);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (fullOrgList) {
      orgMap.clear();
      fullOrgList.forEach((org) => {
        orgMap.set(org.id, {
          name: org.name,
          short_name: org.short_name,
          pi_id: org.pi_id,
          status: org.status,
          tags: org.tags,
        });
      });
    }
  }, [fullOrgList]);

  const userOrgListLoaded = (orgList) => {
    if (!canUserViewAllCores()) {
      setFullOrgList(orgList);
    }
  };

  const fullOrgListLoaded = (orgList) => {
    if (canUserViewAllCores()) {
      setFullOrgList(orgList);
    }
  };

  const updateMapForOrg = (updatedOrg) => {
    orgMap.set(updatedOrg.id, {
      name: updatedOrg.name,
      short_name: updatedOrg.short_name,
      pi_id: updatedOrg.pi_id,
      status: updatedOrg.status,
      tags: updatedOrg.tags,
    });
  };

  const getFilteredList = ({
    excludedIds,
    includeFunc,
    showDeactivated = false,
  }) => {
    return [...orgMap.entries()]
      .map(([key, value]) => {
        return { id: key, ...value };
      })
      .filter(
        (org) =>
          (!excludedIds?.length || !excludedIds.includes(org.id)) &&
          (!includeFunc || includeFunc(org.id)) &&
          (showDeactivated || org.status === OBJECT_STATUS.ACTIVE.key),
      );
  };

  return (
    <OrgContext.Provider
      value={{
        fullOrgList,
        getFilteredList,
      }}>
      {children}
    </OrgContext.Provider>
  );
};

OrgStateProvider.propTypes = {
  children: PropTypes.node,
};

export const useOrgContext = () => useContext(OrgContext);
